import { useEffect, useState } from 'react'
import { Button, Card, CardBody, Input } from '../../design-system/primitives/index.js'
import { useGuestKitchen } from '../../contexts/GuestKitchenContext.jsx'
import OnboardingPreferences from '../onboarding/OnboardingPreferences.jsx'
import StepIndicator from '../../design-system/patterns/StepIndicator.jsx'
import { authenticatedFetch } from '../../utils/auth.js'

const ONBOARDING_STEPS = ['Pantry', 'Recipes', 'Preferences', 'Plan']

export function GuestPantrySurface() {
  const { kitchen, update, saveStatus } = useGuestKitchen()
  const [name, setName] = useState('')
  const [quantity, setQuantity] = useState('')
  const pantry = kitchen?.pantry || []

  function addItem(event) {
    event.preventDefault()
    const itemName = name.trim()
    if (!itemName) return
    update((current) => ({
      ...current,
      pantry: [...current.pantry, { name: itemName, quantity: quantity.trim() || undefined }],
    }))
    setName('')
    setQuantity('')
  }

  function removeItem(index) {
    update((current) => ({
      ...current,
      pantry: current.pantry.filter((_, itemIndex) => itemIndex !== index),
    }))
  }

  return (
    <section className="guest-workspace" aria-labelledby="guest-pantry-title">
      <StepIndicator steps={ONBOARDING_STEPS} activeIndex={0} />
      <header className="guest-workspace__header">
        <div>
          <h1 id="guest-pantry-title">What do you already have?</h1>
          <p>Add the ingredients worth planning around. You can tidy quantities later.</p>
        </div>
        <span>{pantry.length} item{pantry.length === 1 ? '' : 's'}</span>
      </header>
      <Card>
        <CardBody>
          <form className="guest-workspace__quick-add" onSubmit={addItem}>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ingredient" aria-label="Ingredient name" />
            <Input value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder="Quantity (optional)" aria-label="Ingredient quantity" />
            <Button variant="primary" type="submit" disabled={!name.trim() || saveStatus === 'offline'}>Add</Button>
          </form>
          {pantry.length ? (
            <ul className="guest-workspace__list">
              {pantry.map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <span><strong>{item.name}</strong>{item.quantity ? ` · ${item.quantity}` : ''}</span>
                  <Button variant="ghost" onClick={() => removeItem(index)} disabled={saveStatus === 'offline'} aria-label={`Remove ${item.name}`}>×</Button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="guest-workspace__empty">
              <i className="fas fa-box-open" aria-hidden="true" />
              <h2>Your pantry starts here.</h2>
              <p>Add a few staples, produce, or proteins to give the plan something useful to work with.</p>
            </div>
          )}
        </CardBody>
      </Card>
    </section>
  )
}

export function GuestRecipesSurface() {
  const { kitchen, update, saveStatus } = useGuestKitchen()
  const [title, setTitle] = useState('')
  const recipes = kitchen?.recipes || []

  function addRecipe(event) {
    event.preventDefault()
    const recipeTitle = title.trim()
    if (!recipeTitle) return
    update((current) => ({
      ...current,
      recipes: [...current.recipes, { source: 'import', title: recipeTitle, payload: {} }],
    }))
    setTitle('')
  }

  return (
    <section className="guest-workspace" aria-labelledby="guest-recipes-title">
      <StepIndicator steps={ONBOARDING_STEPS} activeIndex={1} />
      <header className="guest-workspace__header">
        <div>
          <h1 id="guest-recipes-title">Give the week a few possibilities.</h1>
          <p>Save a recipe title now. Full URL and image extraction becomes available after you save the kitchen.</p>
        </div>
        <span>{recipes.length} recipe{recipes.length === 1 ? '' : 's'}</span>
      </header>
      <Card>
        <CardBody>
          <form className="guest-workspace__quick-add" onSubmit={addRecipe}>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Recipe title" aria-label="Recipe title" />
            <Button variant="primary" type="submit" disabled={!title.trim() || saveStatus === 'offline'}>Add recipe</Button>
          </form>
          {recipes.length ? (
            <div className="guest-workspace__recipe-grid">
              {recipes.map((recipe, index) => (
                <article key={`${recipe.title}-${index}`}>
                  <span>{recipe.source === 'pack' ? 'PACK' : 'YOUR RECIPE'}</span>
                  <h2>{recipe.title}</h2>
                </article>
              ))}
            </div>
          ) : (
            <div className="guest-workspace__empty">
              <i className="fas fa-book-open" aria-hidden="true" />
              <h2>No recipes yet.</h2>
              <p>Add one meal you already like. A few familiar recipes are enough to start planning.</p>
            </div>
          )}
        </CardBody>
      </Card>
    </section>
  )
}

export function GuestPlanSurface({
  onboardingStep = 4,
  onSelectStep,
  onPreferencesContinue,
  onOnboardingComplete,
}) {
  const { saveStatus } = useGuestKitchen()
  const showPlan = onboardingStep >= 4
  const [plan, setPlan] = useState(null)
  const [status, setStatus] = useState(showPlan ? 'busy' : 'idle')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!showPlan) return undefined
    let active = true

    async function loadOrGenerate() {
      setStatus('busy')
      setError('')
      try {
        const existing = await authenticatedFetch('/guest/plan')
        if (existing.ok) {
          const data = await existing.json()
          if (active && data?.dinners?.length) {
            setPlan(data)
            if (data.dinners.length === 3) onOnboardingComplete?.()
            setStatus('idle')
            return
          }
        } else if (existing.status !== 404) {
          throw new Error('Could not load your dinners')
        }

        const generated = await authenticatedFetch('/guest/plan/generate', { method: 'POST' })
        const data = await generated.json().catch(() => ({}))
        if (!generated.ok) throw new Error(data.detail || 'Could not generate dinners')
        if (!active) return
        setPlan(data)
        if (data.dinners?.length === 3) onOnboardingComplete?.()
        setStatus('idle')
      } catch (loadError) {
        if (!active) return
        setError(loadError.message)
        setStatus('error')
      }
    }

    void loadOrGenerate()
    return () => {
      active = false
    }
  }, [onOnboardingComplete, showPlan])

  if (!showPlan) {
    return (
      <OnboardingPreferences
        onSelectStep={onSelectStep}
        onContinue={onPreferencesContinue}
      />
    )
  }

  return (
    <section className="guest-workspace" aria-labelledby="guest-plan-title">
      <header className="guest-workspace__header">
        <div>
          <h1 id="guest-plan-title">Your plan</h1>
          <p>Dinners starting tomorrow night, built around what you already have.</p>
        </div>
        {saveStatus === 'offline' ? (
          <p className="onboarding-preferences__error" role="status">Reconnect to refresh dinners.</p>
        ) : null}
      </header>
      {status === 'busy' ? (
        <div className="guest-workspace__empty" role="status" aria-live="polite">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <h2>Planning this week’s dinners…</h2>
          <p>Captain Cook is matching your pantry to your recipes.</p>
        </div>
      ) : null}
      {error ? <p className="onboarding-preferences__error" role="alert">{error}</p> : null}
      {plan?.dinners?.length ? (
        <div className="guest-workspace__empty" role="status" aria-live="polite">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <h2>Opening your plan…</h2>
          <p>Your dinners are ready, starting tomorrow night.</p>
        </div>
      ) : null}
    </section>
  )
}
